import {useState, useEffect} from 'react'
import {useNavigate} from 'react-router-dom'
import {_, RequestResult, useCurrentProjectState} from '../../lib'
import {createDispatchActions, selectMethod, selectTempTaskData, selectTheme} from '../../store'
import {
  ProjectContent,
  TempTask,
  TempTaskScheduleActionType,
  getTaskContentName,
  normalizeTaskScheduleReminderList,
  TaskScheduleReminderDTO,
} from '../../model'
import {TaskType} from '../../shared/db'
import {Legend, ButtonCancel, Button} from '..'

export interface ProjectConfirmUpdatePopPropsT {
  projectContent: ProjectContent | null
  closeAction: () => void
}

type TaskScheduleRequest = {
  task: TempTask
  tempSchedule: NonNullable<TempTask['liveEditingScheduleQueue']>[number]
}

// WARNING: do not delete garmin device task in live edit, instead task enabled = false and update spec
// or mobile side need to modify
export const ProjectConfirmUpdatePop = (props: ProjectConfirmUpdatePopPropsT) => {
  const {closeAction, projectContent} = props
  const {color} = selectTheme()
  const {
    doREQUEST_METHOD_UPDATE,
    doREQUEST_METHOD_TASK_INDEX_ORDER_UPDATE,
    doREQUEST_PROJECT_UPDATE,
    doREQUEST_TASK_UPDATE,
    doREQUEST_TASK_TODO_CREATE,
    doREQUEST_TASK_TODO_UPDATE,
    doREQUEST_TASK_TIMER_CREATE,
    doREQUEST_TASK_TIMER_UPDATE,
    doREQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_CREATE,
    doREQUEST_V2_TASK_QUESTIONNAIRE_UPDATE,
    doREQUEST_TASK_GARMIN_DEVICE_CREATE,
    doREQUEST_TASK_GARMIN_DEVICE_UPDATE,
    doREQUEST_TASK_STOPWATCH_GARMIN_STREAM_CREATE,
    doREQUEST_TASK_STOPWATCH_GARMIN_STREAM_UPDATE,
    doREQUEST_TASK_STOPWATCH_MOVESENSE_STREAM_CREATE,
    doREQUEST_TASK_STOPWATCH_MOVESENSE_STREAM_UPDATE,
    doREQUEST_TASK_SCHEDULE_CREATE,
    doREQUEST_TASK_SCHEDULE_UPDATE,
    doREQUEST_TASK_SCHEDULE_DELETE,
    doREQUEST_TASK_SCHEDULE_REMINDER_UPDATE,
    doREQUEST_TASK_EVENT_TRACKER_CREATE,
    doREQUEST_METHOD_FETCH,
    doTEMP_TASK_DATA_DELETE,
  }: any = createDispatchActions() // eslint-disable-line @typescript-eslint/no-explicit-any

  /* ------------------ reqId/completeId ------------------ */
  const [requestUpdateProjectResult, setRequestUpdateProjectResult] = useState<RequestResult | null>(null)
  const [requestMethodUpdateResult, setRequestMethodUpdateResult] = useState<RequestResult | null>(null)
  const [lastTaskRelatedRequestResult, setLastTaskRelatedRequestResult] = useState<RequestResult | null>(null)
  const [lastScheduleRelatedRequestResult, setLastScheduleRelatedRequestResult] = useState<RequestResult | null>(null)
  const [lastEventTrackerRelatedRequestResult, setLastEventTrackerRelatedRequestResult] =
    useState<RequestResult | null>(null)
  const [methodOrderUpdateRequestResult, setMethodOrderUpdateRequestResult] = useState<RequestResult | null>(null)
  const [pendingScheduleRequestList, setPendingScheduleRequestList] = useState<TaskScheduleRequest[]>([])
  const [currentScheduleRequest, setCurrentScheduleRequest] = useState<TaskScheduleRequest | null>(null)
  const [requestCreateTaskScheduleResult, setRequestCreateTaskScheduleResult] = useState<RequestResult | null>(null)
  const [requestUpdateTaskScheduleResult, setRequestUpdateTaskScheduleResult] = useState<RequestResult | null>(null)
  const [requestDeleteTaskScheduleResult, setRequestDeleteTaskScheduleResult] = useState<RequestResult | null>(null)
  const [requestUpdateTaskScheduleReminderResult, setRequestUpdateTaskScheduleReminderResult] =
    useState<RequestResult | null>(null)

  /* ------------------ basic state ------------------ */
  const navigate = useNavigate()
  const methodState = selectMethod()
  const methodId = methodState.id
  const {projectId} = useCurrentProjectState()
  const tempTaskData = selectTempTaskData()

  const tempTaskList = tempTaskData?.taskList || []
  const tempGarminConnectEnable = tempTaskData.garminConnectEnable
  const tempGarminDeviceEnable = tempTaskData.garminDeviceEnable
  const tempGarminStreamEnable = tempTaskData.garminStreamEnable
  const tempMovesenseDeviceEnable = tempTaskData.movesenseDeviceEnable
  const tempDexcomIntegrationId = tempTaskData.dexcomIntegrationId

  const [finishedUpdateProject, setFinishedUpdateProject] = useState(false)
  const [finishedUpdateTasks, setFinishedUpdateTasks] = useState(false)
  const [finishedUpdateMethod, setFinishedUpdateMethod] = useState(false)
  const [finishedUpdateTasksOrder, setFinishedUpdateTasksOrder] = useState(false)
  const [finishedUpdateSchedules, setFinishedUpdateSchedules] = useState(false)
  const [finishedUpdateEventTrackers, setFinishedUpdateEventTrackers] = useState(false)
  const [startUpdateProject, setStartUpdateProject] = useState(false)

  useEffect(() => {
    if (methodOrderUpdateRequestResult?.success) {
      setFinishedUpdateTasksOrder(true)
    }
  }, [methodOrderUpdateRequestResult])

  useEffect(() => {
    if (!finishedUpdateTasks) return
    if (!finishedUpdateMethod) return
    if (!finishedUpdateProject) return
    if (!finishedUpdateTasksOrder) return
    if (!finishedUpdateSchedules) return
    if (!finishedUpdateEventTrackers) return

    // after all api reqeust are done
    doREQUEST_METHOD_FETCH({
      payload: {
        methodId,
      },
    })

    doTEMP_TASK_DATA_DELETE()

    navigate(`/dashboard/${projectId}/?action=summary`)
  }, [
    finishedUpdateTasks,
    finishedUpdateMethod,
    finishedUpdateProject,
    finishedUpdateTasksOrder,
    finishedUpdateSchedules,
    finishedUpdateEventTrackers,
  ])

  // check if garmin related request are all completed
  useEffect(() => {
    if (!startUpdateProject) return
    if (!!requestMethodUpdateResult && !requestMethodUpdateResult.success) return
    setFinishedUpdateMethod(true)
  }, [startUpdateProject, requestMethodUpdateResult])

  // check if task related request are all completed
  useEffect(() => {
    if (lastScheduleRelatedRequestResult?.success) {
      setFinishedUpdateSchedules(true)
    }
  }, [lastScheduleRelatedRequestResult])

  useEffect(() => {
    if (lastEventTrackerRelatedRequestResult?.success) {
      setFinishedUpdateEventTrackers(true)
    }
  }, [lastEventTrackerRelatedRequestResult])

  useEffect(() => {
    if (lastTaskRelatedRequestResult?.success) {
      setFinishedUpdateTasks(true)
    }
  }, [lastTaskRelatedRequestResult])

  useEffect(() => {
    if (!finishedUpdateTasks) return

    // update task schedules after update task is done
    updateTaskSchedules()
    updateTaskEvenTrackers()

    // update task order after update task is done
    updateTaskOrder()
  }, [finishedUpdateTasks])

  // check if project update request is completed
  useEffect(() => {
    if (!startUpdateProject) return
    if (!requestUpdateProjectResult?.success) return
    setFinishedUpdateProject(true)
  }, [startUpdateProject, requestUpdateProjectResult])

  const updateProject = () => {
    if (!projectContent) return
    doREQUEST_PROJECT_UPDATE({
      setRequestResult: setRequestUpdateProjectResult,
      payload: {
        projectId,
        name: projectContent.name || undefined,
        investigator: projectContent.investigator || undefined,
        organization: projectContent.organization || undefined,
        description: projectContent.description || undefined,
        participantInstructions: projectContent.instructionValue || undefined,
        contactDescription: projectContent.contactValue || undefined,
      },
    })
  }

  const updateMethod = () => {
    const {garminConnectEnable, garminDeviceEnable, garminStreamEnable, movesenseDeviceEnable, dexcomIntegrationId} =
      methodState

    if (
      garminDeviceEnable !== tempGarminDeviceEnable ||
      garminConnectEnable !== tempGarminConnectEnable ||
      garminStreamEnable !== tempGarminStreamEnable ||
      movesenseDeviceEnable !== tempMovesenseDeviceEnable ||
      dexcomIntegrationId !== tempDexcomIntegrationId
    ) {
      doREQUEST_METHOD_UPDATE({
        setRequestResult: setRequestMethodUpdateResult,
        payload: {
          methodId,
          garminDeviceEnable: tempGarminDeviceEnable,
          garminConnectEnable: tempGarminConnectEnable,
          garminStreamEnable: tempGarminStreamEnable,
          movesenseDeviceEnable: tempMovesenseDeviceEnable,
          dexcomIntegrationId: tempDexcomIntegrationId,
        },
      })
    } else {
      setFinishedUpdateMethod(true)
    }
  }

  const updateEditedTask = () => {
    const listOfTaskNeedsToBeUpdated = tempTaskList.filter((item) => !!item.actionType)
    if (listOfTaskNeedsToBeUpdated.length) {
      listOfTaskNeedsToBeUpdated.map((task, index, arr) => {
        switch (task.actionType) {
          case 'create':
            return createTaskFromTempTaskList(task, index === arr.length - 1)
          case 'update': {
            return updateTaskFromTempTaskList(task, index === arr.length - 1)
          }
          case 'hide':
            return hideTaskFromTempTaskList(task, index === arr.length - 1)
          case 'show': {
            return showTaskFromTempTaskList(task, index === arr.length - 1)
          }
          default:
            return
        }
      })
    } else {
      setFinishedUpdateTasks(true)
    }
  }

  const createTaskFromTempTaskList = (task: TempTask, isLast: boolean) => {
    const taskContentName = getTaskContentName(task.type)
    const input = {
      name: task[taskContentName]?.name,
      description: task[taskContentName]?.description,
      methodId: task.methodId,
      scheduleRepeat: task.scheduleRepeat,
      scheduleDescription: task.scheduleDescription,
      reminderList: task.reminderList,
      color: task.color,
      tempTaskId: task.id,
    }
    switch (task.type) {
      case 'todo':
        return doREQUEST_TASK_TODO_CREATE({
          setRequestResult: isLast ? setLastTaskRelatedRequestResult : null,
          payload: {
            ...input,
          },
        })
      case 'timer':
        return doREQUEST_TASK_TIMER_CREATE({
          setRequestResult: isLast ? setLastTaskRelatedRequestResult : null,
          payload: {
            ...input,
          },
        })
      case 'questionnaire':
        return doREQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_CREATE({
          setRequestResult: isLast ? setLastTaskRelatedRequestResult : null,
          payload: {
            sectionList: task.questionnaire?.sectionList,
            displayStrategyList: task.displayStrategyList,
            ...input,
          },
        })
      case 'garmin_device':
        return doREQUEST_TASK_GARMIN_DEVICE_CREATE({
          setRequestResult: isLast ? setLastTaskRelatedRequestResult : null,
          payload: {
            methodId: task.methodId,
            garminDevice: task.garminDevice,
          },
        })
      case 'stopwatch_garmin_stream': {
        task.stopwatchGarminStream = _.omit(task.stopwatchGarminStream, [
          'id',
          'taskId',
          'projectId',
          'workspaceId',
          'createdAt',
          'updatedAt',
        ])
        return doREQUEST_TASK_STOPWATCH_GARMIN_STREAM_CREATE({
          setRequestResult: isLast ? setLastTaskRelatedRequestResult : null,
          payload: {
            ...task.stopwatchGarminStream,
            ...input,
          },
        })
      }
      // todo check_stopwatch_movesense_stream
      case 'stopwatch_movesense_stream': {
        task.stopwatchMovesenseStream = _.omit(task.stopwatchMovesenseStream, [
          'id',
          'taskId',
          'projectId',
          'workspaceId',
          'createdAt',
          'updatedAt',
        ])
        return doREQUEST_TASK_STOPWATCH_MOVESENSE_STREAM_CREATE({
          setRequestResult: isLast ? setLastTaskRelatedRequestResult : null,
          payload: {
            ...task.stopwatchMovesenseStream,
            ...input,
          },
        })
      }
      default:
        break
    }
  }

  const updateTaskFromTempTaskList = (task: TempTask, isLast: boolean) => {
    const taskContentName = getTaskContentName(task.type)
    const input = {
      taskId: task.id || undefined,
      name: task[taskContentName]?.name,
      description: task[taskContentName]?.description,
      scheduleRepeat: task.scheduleRepeat,
      scheduleDescription: task.scheduleDescription,
      reminderList: task.reminderList,
      color: task.color,
    }
    switch (task.type) {
      case 'todo':
        return doREQUEST_TASK_TODO_UPDATE({
          setRequestResult: isLast ? setLastTaskRelatedRequestResult : null,
          payload: {
            ...input,
          },
        })
      case 'timer':
        return doREQUEST_TASK_TIMER_UPDATE({
          setRequestResult: isLast ? setLastTaskRelatedRequestResult : null,
          payload: {
            ...input,
          },
        })
      case 'questionnaire':
        delete input.taskId
        return doREQUEST_V2_TASK_QUESTIONNAIRE_UPDATE({
          setRequestResult: isLast ? setLastTaskRelatedRequestResult : null,
          payload: {
            taskId: task.id,
            scheduleRepeat: task.scheduleRepeat,
            scheduleDescription: task.scheduleDescription,
            reminderList: task.reminderList,
            name: task.questionnaire?.name,
            description: task.questionnaire?.description,
            color: task.color,
          },
        })
      case 'garmin_device': {
        doREQUEST_TASK_GARMIN_DEVICE_UPDATE({
          setRequestResult: isLast ? setLastTaskRelatedRequestResult : null,
          payload: {
            taskId: task.id,
            garminDevice: task.garminDevice,
          },
        })
        return doREQUEST_TASK_UPDATE({
          setRequestResult: isLast ? setLastTaskRelatedRequestResult : null,
          payload: {
            taskId: task.id,
            enabled: true,
          },
        })
      }
      case 'stopwatch_garmin_stream': {
        return doREQUEST_TASK_STOPWATCH_GARMIN_STREAM_UPDATE({
          setRequestResult: isLast ? setLastTaskRelatedRequestResult : null,
          payload: {
            ...input,
            timeLimitSeconds: task.stopwatchGarminStream?.timeLimitSeconds,
          },
        })
      }
      // todo check_stopwatch_movesense_stream
      case 'stopwatch_movesense_stream': {
        return doREQUEST_TASK_STOPWATCH_MOVESENSE_STREAM_UPDATE({
          setRequestResult: isLast ? setLastTaskRelatedRequestResult : null,
          payload: {
            ...input,
            timeLimitSeconds: task.stopwatchMovesenseStream?.timeLimitSeconds,
          },
        })
      }
      default:
        break
    }
  }

  const hideTaskFromTempTaskList = (task: TempTask, isLast: boolean) => {
    if (task.type === TaskType.GarminDevice) {
      doREQUEST_TASK_GARMIN_DEVICE_UPDATE({
        setRequestResult: isLast ? setLastTaskRelatedRequestResult : null,
        payload: {
          taskId: task.id,
          garminDevice: task.garminDevice,
        },
      })
    }
    return doREQUEST_TASK_UPDATE({
      setRequestResult: isLast ? setLastTaskRelatedRequestResult : null,
      payload: {
        taskId: task.id,
        enabled: false,
      },
    })
  }

  const showTaskFromTempTaskList = (task: TempTask, isLast: boolean) => {
    updateTaskFromTempTaskList(task, false)

    doREQUEST_TASK_UPDATE({
      setRequestResult: isLast ? setLastTaskRelatedRequestResult : null,
      payload: {
        taskId: task.id,
        enabled: true,
      },
    })
  }

  const updateTaskOrder = () => {
    const taskIdList = tempTaskList
      .filter((task) => {
        return task.enabled && task.type !== TaskType.GarminDevice
      })
      .sort((a, b) => {
        if (a.eventTracker && !b.eventTracker) return -1
        if (!a.eventTracker && b.eventTracker) return 1
        return 0
      })
      .map(({id}) => id)
    if (!taskIdList.length) return

    doREQUEST_METHOD_TASK_INDEX_ORDER_UPDATE({
      setRequestResult: setMethodOrderUpdateRequestResult,
      payload: {
        methodId,
        taskIdList,
      },
    })
  }

  const resolveTaskScheduleIdFromRequestResult = (
    requestResult: RequestResult | null,
    fallbackId?: string,
  ): string | undefined => {
    const payload = requestResult?.result?.payload
    return payload?.replaced?.id ?? payload?.original?.id ?? payload?.id ?? fallbackId
  }

  const resetTaskScheduleRequestResults = () => {
    setRequestCreateTaskScheduleResult(null)
    setRequestUpdateTaskScheduleResult(null)
    setRequestDeleteTaskScheduleResult(null)
    setRequestUpdateTaskScheduleReminderResult(null)
  }

  const finishCurrentScheduleRequest = (requestResult: RequestResult | null) => {
    if (requestResult && !requestResult.success) {
      setLastScheduleRelatedRequestResult(requestResult)
      return
    }

    if (pendingScheduleRequestList.length === 0) {
      setCurrentScheduleRequest(null)
      if (requestResult) {
        setLastScheduleRelatedRequestResult(requestResult)
      } else {
        setFinishedUpdateSchedules(true)
      }
      return
    }

    setCurrentScheduleRequest(null)
  }

  const requestTaskScheduleReminderUpdate = (
    taskScheduleId: string,
    tempSchedule: TaskScheduleRequest['tempSchedule'],
  ) => {
    setRequestUpdateTaskScheduleReminderResult(null)
    doREQUEST_TASK_SCHEDULE_REMINDER_UPDATE({
      setRequestResult: setRequestUpdateTaskScheduleReminderResult,
      payload: {
        taskScheduleId,
        reminderList: normalizeTaskScheduleReminderList(
          tempSchedule.values.reminderList as TaskScheduleReminderDTO[] | undefined,
        ),
      },
    })
  }

  const updateTaskSchedules = () => {
    const tasksNeedsToBeUpdate = tempTaskList.filter((item) => {
      const isScheduleTask = item.liveEditingScheduleQueue && item.liveEditingScheduleQueue.length > 0
      // backend will handle hide task and inactive state of schedules
      const isSupportedType = item.actionType === 'create' || item.actionType === 'update' || item.actionType === 'show'
      return isScheduleTask && isSupportedType
    })

    const scheduleRequests = tasksNeedsToBeUpdate.flatMap((task) => {
      return (task.liveEditingScheduleQueue ?? [])
        .filter((tempSchedule) => tempSchedule.action)
        .sort((a, b) => {
          // push delete action to the tail of operations, because ScheduleTask must have at least one schedule
          // if create action was order after all delete action, the last schedule delete will casue API error.
          if (a.action === TempTaskScheduleActionType.Delete && b.action !== TempTaskScheduleActionType.Delete) {
            return 1
          }
          if (a.action !== TempTaskScheduleActionType.Delete && b.action === TempTaskScheduleActionType.Delete) {
            return -1
          }
          return 0
        })
        .map((tempSchedule) => ({task, tempSchedule}))
    })

    if (!scheduleRequests.length) {
      setFinishedUpdateSchedules(true)
      return
    }

    resetTaskScheduleRequestResults()
    setLastScheduleRelatedRequestResult(null)
    setPendingScheduleRequestList(scheduleRequests)
    setCurrentScheduleRequest(null)
  }

  useEffect(() => {
    if (currentScheduleRequest) return
    if (pendingScheduleRequestList.length === 0) return

    const [nextScheduleRequest, ...remainingScheduleRequests] = pendingScheduleRequestList
    setCurrentScheduleRequest(nextScheduleRequest)
    setPendingScheduleRequestList(remainingScheduleRequests)
  }, [currentScheduleRequest, pendingScheduleRequestList])

  useEffect(() => {
    if (!currentScheduleRequest) return

    const {task, tempSchedule} = currentScheduleRequest
    switch (tempSchedule.action) {
      case TempTaskScheduleActionType.Create: {
        setRequestCreateTaskScheduleResult(null)
        doREQUEST_TASK_SCHEDULE_CREATE({
          setRequestResult: setRequestCreateTaskScheduleResult,
          payload: {
            schedule: {
              ...tempSchedule.values,
              taskId: task.id, // important to set the taskId at the last step to overwrite the taskId in tempSchedule.values
            },
          },
        })
        break
      }
      case TempTaskScheduleActionType.Update: {
        setRequestUpdateTaskScheduleResult(null)
        doREQUEST_TASK_SCHEDULE_UPDATE({
          setRequestResult: setRequestUpdateTaskScheduleResult,
          payload: {
            liveEditing: true,
            schedule: {
              ...tempSchedule.values,
              taskId: task.id, // important to set the taskId at the last step to overwrite the taskId in tempSchedule.values
            },
          },
        })
        break
      }
      case TempTaskScheduleActionType.UpdateReminderList: {
        if (!tempSchedule.values.id) {
          finishCurrentScheduleRequest(null)
          break
        }

        requestTaskScheduleReminderUpdate(tempSchedule.values.id, tempSchedule)
        break
      }
      case TempTaskScheduleActionType.Delete: {
        setRequestDeleteTaskScheduleResult(null)
        doREQUEST_TASK_SCHEDULE_DELETE({
          setRequestResult: setRequestDeleteTaskScheduleResult,
          payload: {
            liveEditing: true,
            id: tempSchedule.values.id,
          },
        })
        break
      }
    }
  }, [currentScheduleRequest])

  useEffect(() => {
    if (!requestCreateTaskScheduleResult) return
    if (currentScheduleRequest?.tempSchedule.action !== TempTaskScheduleActionType.Create) return

    const {tempSchedule} = currentScheduleRequest
    if (!requestCreateTaskScheduleResult.success) {
      finishCurrentScheduleRequest(requestCreateTaskScheduleResult)
      return
    }

    const taskScheduleId = resolveTaskScheduleIdFromRequestResult(
      requestCreateTaskScheduleResult,
      tempSchedule.values.id,
    )
    if (taskScheduleId && tempSchedule.reminderListChanged) {
      requestTaskScheduleReminderUpdate(taskScheduleId, tempSchedule)
      return
    }

    finishCurrentScheduleRequest(requestCreateTaskScheduleResult)
  }, [requestCreateTaskScheduleResult])

  useEffect(() => {
    if (!requestUpdateTaskScheduleResult) return
    if (currentScheduleRequest?.tempSchedule.action !== TempTaskScheduleActionType.Update) return

    const {tempSchedule} = currentScheduleRequest
    if (!requestUpdateTaskScheduleResult.success) {
      finishCurrentScheduleRequest(requestUpdateTaskScheduleResult)
      return
    }

    const taskScheduleId = resolveTaskScheduleIdFromRequestResult(
      requestUpdateTaskScheduleResult,
      tempSchedule.values.id,
    )
    if (taskScheduleId && tempSchedule.reminderListChanged) {
      requestTaskScheduleReminderUpdate(taskScheduleId, tempSchedule)
      return
    }

    finishCurrentScheduleRequest(requestUpdateTaskScheduleResult)
  }, [requestUpdateTaskScheduleResult])

  useEffect(() => {
    if (!requestDeleteTaskScheduleResult) return
    if (currentScheduleRequest?.tempSchedule.action !== TempTaskScheduleActionType.Delete) return

    finishCurrentScheduleRequest(requestDeleteTaskScheduleResult)
  }, [requestDeleteTaskScheduleResult])

  useEffect(() => {
    if (!requestUpdateTaskScheduleReminderResult) return
    if (!currentScheduleRequest) return

    finishCurrentScheduleRequest(requestUpdateTaskScheduleReminderResult)
  }, [requestUpdateTaskScheduleReminderResult])

  const updateTaskEvenTrackers = () => {
    const tasksNeedsToBeUpdate = tempTaskList.filter((item) => {
      const isEventTracker = item.eventTracker != undefined
      // event tracker should have no update need, hide will be a task action and handled by backend
      const isSupportedType = item.actionType === 'create'
      return isEventTracker && isSupportedType
    })
    if (tasksNeedsToBeUpdate.length) {
      tasksNeedsToBeUpdate.map((task, index, arr) => {
        const isLastTask = index === arr.length - 1
        if (task.actionType == 'create') {
          doREQUEST_TASK_EVENT_TRACKER_CREATE({
            setRequestResult: isLastTask ? setLastEventTrackerRelatedRequestResult : null,
            payload: {
              eventTracker: {
                taskId: task.id,
              },
            },
          })
        }
      })
    } else {
      setFinishedUpdateEventTrackers(true)
    }
  }

  const handleEditConfirmPopClick = () => {
    setStartUpdateProject(true)
    updateEditedTask()
    updateMethod()
    updateProject()
  }

  const handleCloseAction = () => closeAction()

  const NoticeContents = () => {
    return (
      <>
        <div>
          This will automatically update the participant app. Depending on participants’ internet connection, the update
          time may be different.
        </div>
        <div css={{height: '24px'}} />
        <div>
          Note: Any tasks with changed schedules may show up incorrectly for the first day (ie: If you changed a
          schedule on a task and that participant has already completed or seen that task)
        </div>
      </>
    )
  }

  return (
    <div
      data-testid='task_confirm_delete_pop'
      css={{
        width: '100vw',
        height: '100vh',
        position: 'fixed',
        top: 0,
        left: 0,
        background: '#00000040',
        zIndex: 99999,
      }}>
      <div
        css={{
          position: 'absolute',
          top: 'calc(50vh - 145px)',
          left: 'calc(50vw - 300px)',
          width: '600px',
          borderRadius: '5px',
          padding: '30px',
          background: color.white,
        }}>
        <div
          css={{
            display: 'flex',
            alignItems: 'top',
            justifyContent: 'space-between',
            position: 'relative',
          }}>
          <Legend
            css={{
              marginBottom: '16px',
            }}>
            Save changes and update participant app?
          </Legend>

          <ButtonCancel onClick={handleCloseAction} size={'large'} />
        </div>
        {NoticeContents()}
        {/* divider */}
        <div
          css={{
            width: '100%',
            height: '1px',
            background: color.grey_60,
            marginTop: '32px',
            marginBottom: '20px',
          }}></div>
        <div
          css={{
            width: '100%',
            display: 'flex',
            justifyContent: 'right',
          }}>
          <Button
            onClick={handleCloseAction}
            btnPadding='large'
            btnColor='grey_20'
            textColor={color.grey_600}
            children={'Cancel'}
          />
          <div css={{width: '12px'}} />
          <Button
            onClick={handleEditConfirmPopClick}
            btnPadding='large'
            btnColor='primary'
            children={'Save and Update'}
          />
        </div>
      </div>
    </div>
  )
}
